import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from './firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiBook, FiArrowRight, FiPlus, FiX, FiCode, FiSave, FiTrash2, FiFilter } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import { useTheme } from '../context/ThemeContext';
import { BannerAd } from './AdSense';
import AuthLockedView from './AuthLockedView';
import BackButton from './BackButton';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'snaped4338@gmail.com';

const CATEGORIES = ['All', 'Programming', 'Web Dev', 'DSA', 'Database', 'Networks', 'AI/ML', 'Cloud', 'Roadmaps', 'General'];

const TutorialCard = ({ tutorial, onDelete, isAdmin, navigate }) => {
  const { isDarkMode } = useTheme();
  const accent = '#22c55e';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -8, boxShadow: isDarkMode ? '0 20px 40px rgba(0,0,0,0.4)' : '0 20px 40px rgba(0,0,0,0.06)' }}
      style={{
        borderRadius: 24,
        background: 'var(--bg-card)',
        border: `1px solid var(--border)`,
        backdropFilter: 'blur(16px)',
        overflow: 'hidden', cursor: 'pointer',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative'
      }}
    >
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 24 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 16, flexShrink: 0,
            background: tutorial.type === 'roadmap' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.1)',
            border: `1px solid ${tutorial.type === 'roadmap' ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.2)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: tutorial.type === 'roadmap' ? '#22c55e' : '#22c55e'
          }}>
            {tutorial.type === 'roadmap' ? <FiFilter size={24} /> : <FiCode size={24} />}
          </div>
          {tutorial.category && (
            <span style={{
              fontSize: '0.75rem', padding: '6px 12px', borderRadius: 50,
              background: tutorial.type === 'roadmap' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.1)',
              border: `1px solid ${tutorial.type === 'roadmap' ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.2)'}`,
              color: tutorial.type === 'roadmap' ? '#22c55e' : '#22c55e', fontWeight: 800, letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              {tutorial.type === 'roadmap' ? 'Roadmap' : tutorial.category}
            </span>
          )}
        </div>

        <h3 style={{ margin: '0 0 12px', color: 'var(--text-main)', fontSize: '1.25rem', fontWeight: 900, lineHeight: 1.3, letterSpacing: '-0.02em' }}>
          {tutorial.title}
        </h3>
        <p style={{
          margin: '0 0 28px', color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.6,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          {tutorial.description}
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <motion.button
            whileHover={{ scale: 1.02, backgroundColor: '#16a34a' }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(`/tutorial/${tutorial.id}`)}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 14, border: 'none',
              background: tutorial.type === 'roadmap' ? '#22c55e' : '#22c55e', color: '#fff',
              fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: `0 10px 20px ${tutorial.type === 'roadmap' ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.2)'}`
            }}
          >
            {tutorial.type === 'roadmap' ? 'View Roadmap' : 'Read Article'} <FiArrowRight size={16} />
          </motion.button>

          {isAdmin && (
            <motion.button
              whileHover={{ scale: 1.05, background: 'rgba(239, 68, 68, 0.2)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onDelete(tutorial.id)}
              style={{
                width: 44, height: 44, borderRadius: 14,
                border: '1px solid rgba(239,68,68,0.2)',
                background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <FiTrash2 size={18} />
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const TutorialCards = () => {
  const { isDarkMode } = useTheme();
  const [tutorials, setTutorials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTutorial, setNewTutorial] = useState({ title: '', description: '', category: 'General', topics: [{ title: 'Introduction', content: '' }] });
  const user = auth.currentUser;
  const isAdmin = user?.email === ADMIN_EMAIL;
  const navigate = useNavigate();

  const c = {
    bg: 'var(--bg-main)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    accent: 'var(--accent)',
    card: 'var(--bg-card)',
    border: 'var(--border)',
    input: 'var(--bg-surface)'
  };

  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        const snap = await getDocs(collection(db, 'posts'));
        setTutorials(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchTutorials();
  }, []);

  const filtered = useMemo(() => tutorials.filter(t => {
    const matchesSearch = t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat = selectedCategory === 'All' || t.category === selectedCategory;
    return matchesSearch && matchesCat;
  }), [tutorials, searchQuery, selectedCategory]);

  const handleAdd = async () => {
    if (!newTutorial.title || !newTutorial.description) return;
    try {
      const docRef = await addDoc(collection(db, 'posts'), { ...newTutorial, createdAt: serverTimestamp(), createdBy: user?.uid, type: newTutorial.category === 'Roadmaps' ? 'roadmap' : 'tutorial' });
      setTutorials(prev => [...prev, { id: docRef.id, ...newTutorial, type: newTutorial.category === 'Roadmaps' ? 'roadmap' : 'tutorial' }]);
      setNewTutorial({ title: '', description: '', category: 'General', topics: [{ title: 'Introduction', content: '' }] });
      setShowAddForm(false);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tutorial?')) return;
    try {
      await deleteDoc(doc(db, 'posts', id));
      setTutorials(prev => prev.filter(t => t.id !== id));
    } catch (err) { console.error(err); }
  };

  const inputStyle = {
    width: '100%', padding: '16px 20px', borderRadius: 16,
    border: `1px solid ${c.border}`, background: c.input,
    color: c.text, fontSize: '0.95rem', outline: 'none', fontFamily: 'inherit',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ minHeight: '100vh', background: c.bg, color: c.text, paddingTop: '72px' }}>
      {/* Back Button */}
      <div style={{ position: 'absolute', top: '88px', left: '24px', zIndex: 40 }}>
        <BackButton to="/home" label="Back to Home" />
      </div>
      {!user && <AuthLockedView />}
      <div style={{ filter: !user ? 'blur(8px)' : 'none', pointerEvents: !user ? 'none' : 'auto', transition: 'filter 0.3s ease' }}>
        {/* Hero */}
        <section className="hero-section" style={{ padding: '120px 24px 64px', position: 'relative', overflow: 'hidden' }}>
          {/* Decorative Glows */}
          <div style={{ position: 'absolute', top: -100, left: '20%', width: 600, height: 600, background: 'rgba(34,197,94,0.08)', filter: 'blur(120px)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -100, right: '10%', width: 400, height: 400, background: 'rgba(34,197,94,0.05)', filter: 'blur(100px)', borderRadius: '50%', pointerEvents: 'none' }} />

          <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, cubicBezier: [0.16, 1, 0.3, 1] }}
            >
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                padding: '8px 20px', borderRadius: 50,
                background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)',
                color: '#22c55e', fontSize: '0.8rem', fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 32
              }}>
                <FiBook size={16} /> Knowledge Academy
              </div>
              <h1 style={{ margin: '0 0 24px', fontSize: 'clamp(3rem, 8vw, 5.5rem)', fontWeight: 900, letterSpacing: '-0.05em', color: 'var(--text-main)', lineHeight: 1 }}>
                Code<span style={{ color: 'var(--accent)' }}>Campus</span>
              </h1>
              <p style={{ margin: '0 auto 48px', color: 'var(--text-muted)', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', maxWidth: 640, lineHeight: 1.6, fontWeight: 500 }}>
                Unlock your potential with premium tutorials, expert-led guides, and modern tech resources.
              </p>
            </motion.div>

            {/* Search + Add */}
            <div className="search-container" style={{
              display: 'flex', gap: 16, maxWidth: 800, margin: '0 auto', flexWrap: 'wrap',
              background: 'var(--bg-card)', padding: 14, borderRadius: 28, border: '1px solid var(--border)',
              backdropFilter: 'blur(24px)', boxShadow: 'var(--card-shadow)'
            }}>
              <div style={{ flex: 1, position: 'relative', minWidth: 280 }}>
                <FiSearch style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: '#22c55e' }} size={22} />
                <input
                  style={{ ...inputStyle, paddingLeft: 56, borderRadius: 20, border: 'none', background: 'rgba(255,255,255,0.03)' }}
                  placeholder="Search tutorials, languages, concepts..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              {isAdmin && (
                <motion.button
                  whileHover={{ scale: 1.02, backgroundColor: '#16a34a' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowAddForm(true)}
                  style={{
                    padding: '0 28px', borderRadius: 20, background: '#22c55e', color: '#fff',
                    border: 'none', fontWeight: 900, cursor: 'pointer', display: 'flex',
                    alignItems: 'center', gap: 10, fontSize: '1rem',
                    boxShadow: '0 10px 15px -3px rgba(34,197,94,0.3)'
                  }}
                >
                  <FiPlus size={20} /> New Article
                </motion.button>
              )}
            </div>

            {/* Category Filter */}
            <div className="category-filter" style={{ display: 'flex', gap: 12, marginTop: 40, flexWrap: 'wrap', justifyContent: 'center' }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    padding: '10px 24px', borderRadius: 16,
                    border: '1px solid',
                    borderColor: selectedCategory === cat ? 'var(--accent)' : 'var(--border)',
                    background: selectedCategory === cat ? 'var(--bg-surface)' : 'var(--bg-card)',
                    color: selectedCategory === cat ? 'var(--accent)' : 'var(--text-muted)',
                    fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    letterSpacing: '0.02em'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <BannerAd />
        </div>

        {/* Tutorial Grid */}
        <section style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px 100px' }}>
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 24 }}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} style={{ height: 320, borderRadius: 24, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 4, height: 24, background: 'var(--accent)', borderRadius: 2 }} />
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 900, margin: 0, letterSpacing: '-0.02em', color: 'var(--text-main)' }}>Available Resources</h2>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 700, background: 'var(--bg-card)', padding: '4px 10px', borderRadius: 8 }}>{filtered.length} items</span>
                </div>
              </div>

              <motion.div className="tutorial-grid" layout style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 24 }}>
                <AnimatePresence>
                  {filtered.map((tut, i) => {
                    const items = [<TutorialCard key={tut.id} tutorial={tut} onDelete={handleDelete} isAdmin={isAdmin} navigate={navigate} />];
                    if ((i + 1) % 6 === 0 && i < filtered.length - 1) {
                      items.push(<div key={`ad-${i}`} style={{ gridColumn: '1 / -1', margin: '16px 0' }}><BannerAd /></div>);
                    }
                    return items;
                  })}
                </AnimatePresence>
              </motion.div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '100px 24px', background: 'var(--bg-card)', borderRadius: 32, border: `1px solid var(--border)` }}>
              <div style={{ width: 80, height: 80, borderRadius: 24, background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 24px' }}>
                <FiBook size={40} />
              </div>
              <h3 style={{ color: 'var(--text-main)', fontSize: '1.5rem', margin: '0 0 12px', fontWeight: 900 }}>No resources found</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '1rem', maxWidth: 400, margin: '0 auto' }}>Try refining your search or explore different categories to find what you're looking for.</p>
            </div>
          )}
        </section>

        <AnimatePresence>
          {showAddForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
              onClick={() => setShowAddForm(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
                onClick={e => e.stopPropagation()}
                style={{ background: 'var(--bg-main)', borderRadius: 32, padding: 40, width: '100%', maxWidth: 640, border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 40px 100px -20px rgba(0,0,0,0.8)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
                  <div>
                    <h3 style={{ margin: 0, color: 'var(--text-main)', fontWeight: 900, fontSize: '1.75rem', letterSpacing: '-0.03em' }}>Create New Tutorial</h3>
                    <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Share your knowledge with the SnapED community.</p>
                  </div>
                  <button onClick={() => setShowAddForm(false)} style={{ background: 'var(--bg-card)', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', width: 44, height: 44, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}><FiX size={24} /></button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '0.05em', textTransform: 'uppercase', marginLeft: 4 }}>Article Title</label>
                    <input style={{ ...inputStyle, background: 'var(--bg-surface)' }} placeholder="e.g. Master Data Structures with Python" value={newTutorial.title} onChange={e => setNewTutorial(p => ({ ...p, title: e.target.value }))} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '0.05em', textTransform: 'uppercase', marginLeft: 4 }}>Summary Description</label>
                    <textarea style={{ ...inputStyle, background: 'var(--bg-surface)', minHeight: 100, resize: 'none' }} placeholder="A brief overview of what this tutorial covers..." value={newTutorial.description} onChange={e => setNewTutorial(p => ({ ...p, description: e.target.value }))} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '0.05em', textTransform: 'uppercase', marginLeft: 4 }}>Category</label>
                      <select style={{ ...inputStyle, background: 'var(--bg-surface)', cursor: 'pointer' }} value={newTutorial.category} onChange={e => setNewTutorial(p => ({ ...p, category: e.target.value }))}>
                        {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c} style={{ background: 'var(--bg-main)' }}>{c}</option>)}
                      </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '0.05em', textTransform: 'uppercase', marginLeft: 4 }}>Initial Topic</label>
                      <input style={{ ...inputStyle, background: 'var(--bg-surface)' }} value={newTutorial.topics[0].title} onChange={e => setNewTutorial(p => ({ ...p, topics: [{ ...p.topics[0], title: e.target.value }] }))} placeholder="Introduction" />
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 900, color: 'var(--text-main)', letterSpacing: '0.05em', textTransform: 'uppercase', marginLeft: 4 }}>Article Content (Markdown Supported)</label>
                    <textarea style={{ ...inputStyle, background: 'var(--bg-surface)', minHeight: 240, resize: 'vertical' }} placeholder="Write your curriculum, notes, and code snippets here..." value={newTutorial.topics[0].content} onChange={e => setNewTutorial(p => ({ ...p, topics: [{ ...p.topics[0], content: e.target.value }] }))} />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02, backgroundColor: '#16a34a', boxShadow: '0 20px 40px rgba(34,197,94,0.3)' }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAdd}
                    style={{
                      padding: '18px', borderRadius: 20,
                      background: '#22c55e', color: '#fff',
                      border: 'none', fontWeight: 900, fontSize: '1.1rem',
                      cursor: 'pointer', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', gap: 12, marginTop: 16,
                      boxShadow: '0 15px 30px rgba(34,197,94,0.2)'
                    }}
                  >
                    <FiSave size={24} /> Publish Academy Article
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <Footer />

        <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
      `}</style>
      </div>
    </div>
  );
};

export default TutorialCards;
